import { useCallback, useEffect, useMemo, useState } from 'react';
import GameShell from '../../shared/ui/GameShell';
import LoadingScreen from '../../shared/ui/LoadingScreen';
import ErrorScreen from '../../shared/ui/ErrorScreen';
import FinishedDaily from '../../shared/ui/FinishedDaily';
import { finish as recordOutcome, isFinished } from '../../shared/dailySession';
import { useDailySnapshot, usePersistedState } from '../../shared/useDailySession';
import DailyDoneOverlay from '../../shared/ui/DailyDoneOverlay';
import Keyboard from '../../shared/ui/Keyboard';
import { loadTodaysPuzzle, formatDateLabel } from '../../shared/daily';
import { isValidWordFull } from '../../shared/dictionary';
import {
  normalizeRootwords,
  type NormalizedRootwords,
  type RootwordsLevel,
  type ValidWord,
} from './types';
import './rootwords.css';

export default function RootwordsGame() {
  const [data, setData] = useState<{
    puzzle: NormalizedRootwords;
    dateKey: string;
    isToday: boolean;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysPuzzle<RootwordsLevel>('rootwords')
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setLoadError('No daily puzzle published for Rootwords yet.');
          return;
        }
        const puzzle = normalizeRootwords(res.data);
        if (!puzzle) {
          setLoadError('Today\'s Rootwords puzzle is malformed.');
          return;
        }
        setData({ puzzle, dateKey: res.dateKey, isToday: res.isToday });
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} gameId="rootwords" />;
  if (!data) {
    return (
      <GameShell gameId="rootwords">
        <LoadingScreen label="Fetching today's puzzle…" />
      </GameShell>
    );
  }

  // A daily is one attempt. Once the round has resolved, show the result
  // instead of mounting a fresh board.
  if (isFinished('rootwords', data.dateKey)) {
    return (
      <FinishedDaily gameId="rootwords" dateKey={data.dateKey} isToday={data.isToday} />
    );
  }

  return (
    <RootwordsPlay
      key={data.dateKey}
      puzzle={data.puzzle}
      dateKey={data.dateKey}
      isToday={data.isToday}
    />
  );
}

function RootwordsPlay({
  puzzle,
  dateKey,
  isToday,
}: {
  puzzle: NormalizedRootwords;
  dateKey: string;
  isToday: boolean;
}) {
  const [currentInput, setCurrentInput] = useState('');
  const { restored, save } = useDailySnapshot<{ found: string[]; hintShown: boolean }>(
    'rootwords',
    dateKey,
  );

  const [foundWords, setFoundWords] = useState<Set<string>>(
    () => new Set(restored?.found ?? []),
  );
  const [hintShown, setHintShown] = useState(restored?.hintShown ?? false);
  const [completed, setCompleted] = useState(false);

  usePersistedState(save, { found: [...foundWords], hintShown }, !completed);
  const [shake, setShake] = useState(false);
  const [toast, setToast] = useState<{ msg: string; style: 'info' | 'success' | 'error' } | null>(
    null,
  );

  const showToast = useCallback((msg: string, style: 'info' | 'success' | 'error' = 'info') => {
    setToast({ msg, style });
    setTimeout(() => setToast(null), 1600);
  }, []);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  }, []);

  useEffect(() => {
    if (completed) recordOutcome('rootwords', dateKey, 'won', { subtitle: 'Root cracked.' });
  }, [completed, dateKey]);

  const curatedSet = useMemo(
    () => new Set(puzzle.validWords.map((v) => v.word)),
    [puzzle.validWords],
  );

  const curatedFound = useMemo(
    () => puzzle.validWords.filter((v) => foundWords.has(v.word)),
    [puzzle.validWords, foundWords],
  );

  const extraFound = useMemo(
    () =>
      [...foundWords].filter((w) => !curatedSet.has(w)).sort(),
    [foundWords, curatedSet],
  );

  const checkCompletion = useCallback(
    (set: Set<string>) => {
      if (set.size >= puzzle.targetCount) setCompleted(true);
    },
    [puzzle.targetCount],
  );

  const submit = useCallback(async () => {
    if (completed) return;
    const candidate = currentInput.toUpperCase();
    if (candidate.length < 3) {
      triggerShake();
      showToast('At least 3 letters', 'error');
      return;
    }
    if (!candidate.includes(puzzle.root)) {
      triggerShake();
      showToast(`Must contain ${puzzle.root}`, 'error');
      return;
    }
    if (foundWords.has(candidate)) {
      triggerShake();
      showToast('Already found', 'info');
      return;
    }

    const curated = puzzle.validWords.find((v) => v.word === candidate);
    if (curated) {
      const next = new Set(foundWords).add(candidate);
      setFoundWords(next);
      setCurrentInput('');
      showToast(`★ ${candidate} — ${curated.definition}`, 'success');
      checkCompletion(next);
      return;
    }

    const valid = await isValidWordFull(candidate);
    if (valid) {
      const next = new Set(foundWords).add(candidate);
      setFoundWords(next);
      setCurrentInput('');
      showToast(`+${candidate}`, 'success');
      checkCompletion(next);
      return;
    }

    triggerShake();
    showToast('Not in dictionary', 'error');
  }, [
    completed,
    currentInput,
    puzzle,
    foundWords,
    triggerShake,
    showToast,
    checkCompletion,
  ]);

  const useHint = useCallback(() => {
    if (completed || hintShown) return;
    const unfound = puzzle.validWords.filter((v) => !foundWords.has(v.word));
    if (unfound.length === 0) return;
    const pick = unfound.find((v) => v.rarity === 'common') ?? unfound[0];
    setHintShown(true);
    const next = new Set(foundWords).add(pick.word);
    setFoundWords(next);
    showToast(`Hint: ${pick.word} — ${pick.definition}`, 'info');
    checkCompletion(next);
  }, [completed, hintShown, puzzle.validWords, foundWords, showToast, checkCompletion]);

  const onLetter = useCallback(
    (letter: string) => {
      if (completed) return;
      setCurrentInput((prev) => prev + letter);
    },
    [completed],
  );
  const onDelete = useCallback(() => {
    if (completed) return;
    setCurrentInput((prev) => prev.slice(0, -1));
  }, [completed]);

  const submitEnabled = currentInput.length >= 3 && !completed;

  return (
    <GameShell
      gameId="rootwords"
      subtitle={`${formatDateLabel(dateKey, isToday)} • ${foundWords.size}/${puzzle.targetCount} found`}
    >
      <div className="rw-board">
        <div className="rw-root-tile">
          <span className="rw-root-text">{puzzle.root}</span>
          {puzzle.rootMeaning && <span className="rw-root-meaning">"{puzzle.rootMeaning}"</span>}
        </div>

        <p className="rw-instructions">
          Find any word containing <strong>{puzzle.root}</strong>. Curated picks are starred and
          reveal their meaning.
        </p>

        <FoundLists curated={curatedFound} extras={extraFound} />

        <div className={'rw-input' + (shake ? ' is-shake' : '')}>
          <span className={currentInput ? 'rw-input-text' : 'rw-input-placeholder'}>
            {currentInput || 'Type a word…'}
          </span>
          {currentInput && (
            <button
              className="rw-input-clear"
              onClick={() => setCurrentInput('')}
              aria-label="Clear"
            >
              ✕
            </button>
          )}
        </div>

        {toast && <div className={`rw-toast rw-toast-${toast.style}`}>{toast.msg}</div>}

        <Keyboard
          onLetter={onLetter}
          onDelete={onDelete}
          onSubmit={submit}
          submitEnabled={submitEnabled}
          onHint={useHint}
          hintEnabled={!hintShown && !completed}
        />

        {completed && (
          <DailyDoneOverlay
            dateKey={dateKey}
            isToday={isToday}
            subtitle={`Found ${foundWords.size} of ${puzzle.validWords.length} curated words.`}
          />
        )}
      </div>
    </GameShell>
  );
}

function FoundLists({ curated, extras }: { curated: ValidWord[]; extras: string[] }) {
  if (curated.length === 0 && extras.length === 0) {
    return <div className="rw-empty">No words found yet. Start typing!</div>;
  }
  return (
    <div className="rw-found-cols">
      <div className="rw-found-col">
        <div className="rw-found-head">OTHER</div>
        {extras.length === 0 && <div className="rw-found-dash">—</div>}
        {extras.map((w) => (
          <div key={w} className="rw-found-pill">
            {w}
          </div>
        ))}
      </div>
      <div className="rw-found-col">
        <div className="rw-found-head">★ CURATED</div>
        {curated.length === 0 && <div className="rw-found-dash">—</div>}
        {curated.map((v) => (
          <div key={v.word} className="rw-found-pill is-curated" title={v.definition}>
            {v.word}
          </div>
        ))}
      </div>
    </div>
  );
}
